import type { IzvirnaVrsticaKnjizbe, KnjizbaZaPrikaz } from '@koda/shared';
import { useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { datum, znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';

interface Lastnosti {
  podjetje: string;
  knjizba: KnjizbaZaPrikaz;
  naZapri: () => void;
}

/**
 * Okno z izvirno vrstico knjižbe, kot je bila v datoteki uvoza (samo
 * skrbnik; docs/ARHITEKTURA.md, 5.6). Pove, od kod je vrstica – iz
 * arhivirane izvirne datoteke po lokaciji ali iz vrednosti, shranjenih pri
 * knjižbi –, in izpiše izvirna imena stolpcev z vrednostmi. Napake strežnika
 * (izbrisan ali poškodovan izvirnik) pokaže z njegovim besedilom.
 */
export function IzvirnaVrstica({ podjetje, knjizba, naZapri }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const [vrstica, setVrstica] = useState<IzvirnaVrsticaKnjizbe | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  useEffect(() => {
    let prekinjeno = false;
    setVrstica(null);
    setNapaka(null);
    api
      .izvirnaVrstica(podjetje, knjizba.id)
      .then((v) => {
        if (!prekinjeno) setVrstica(v);
      })
      .catch((e: unknown) => {
        if (!prekinjeno) setNapaka(e instanceof Error ? e.message : b.izvirnaVrsticaNapaka);
      });
    return () => {
      prekinjeno = true;
    };
  }, [podjetje, knjizba.id, b.izvirnaVrsticaNapaka]);

  useEffect(() => {
    const naTipko = (d: KeyboardEvent): void => {
      if (d.key === 'Escape') naZapri();
    };
    document.addEventListener('keydown', naTipko);
    return () => {
      document.removeEventListener('keydown', naTipko);
    };
  }, [naZapri]);

  const lokacija =
    vrstica?.lokacija === null || vrstica === null
      ? null
      : vrstica.lokacija.oblika === 'xlsx'
        ? b.lokacijaXlsx(vrstica.lokacija.zavihek, vrstica.lokacija.vrstica)
        : b.lokacijaCsv(vrstica.lokacija.zapis);

  return (
    <div
      className="prekrivalo-izvirne-vrstice"
      onClick={(d) => {
        if (d.target === d.currentTarget) naZapri();
      }}
    >
      <section className="okno-izvirne-vrstice" role="dialog" aria-modal="true" aria-labelledby="izvirna-vrstica-naslov">
        <header>
          <div>
            <h2 id="izvirna-vrstica-naslov">{b.izvirnaVrstica}</h2>
            <p className="podnaslov">
              {datum(knjizba.datum)} · <code>{knjizba.konto}</code>
              {knjizba.dokument ? ` · ${knjizba.dokument}` : ''} · {znesek(knjizba.znesek)}
            </p>
          </div>
          <button type="button" className="zapri" aria-label={b.zapriOkno} onClick={naZapri}>
            ×
          </button>
        </header>
        {napaka !== null && <p className="napaka vir">{napaka}</p>}
        {napaka === null && vrstica === null && <p className="vir">{b.beremIzvirnoVrstico}</p>}
        {vrstica !== null && (
          <>
            <p className="vir">
              <strong>{vrstica.vir === 'arhiv' ? b.virArhiv : b.virKnjizba}</strong>
              {vrstica.datoteka !== null && (
                <>
                  {' · '}
                  {b.izvirnaDatoteka}:{' '}
                  <a href={api.naslovIzvirnika(podjetje, vrstica.datoteka.id)} download={vrstica.datoteka.ime}>
                    {vrstica.datoteka.ime}
                  </a>
                </>
              )}
              {lokacija !== null && <> · {lokacija}</>}
            </p>
            <div className="vsebina">
              <table>
                <thead>
                  <tr>
                    <th>{b.glavaStolpec}</th>
                    <th>{b.glavaVrednost}</th>
                  </tr>
                </thead>
                <tbody>
                  {vrstica.stolpci.map((s, i) => (
                    <tr key={`${String(i)}-${s.ime}`}>
                      <td className="ime">{s.ime}</td>
                      {s.vrednost === null ? (
                        <td className="vrednost manjka" title={b.stolpcaNiBilo}>
                          —
                        </td>
                      ) : (
                        <td className="vrednost">{s.vrednost}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
